/**
 * SpreadTab Component
 *
 * Displays spread market data including:
 * - Current spread lines from all bookmakers
 * - Spread movement analysis
 * - Key number visualization
 */

import type { BettingVolumeResponse } from '../../../api/client';

interface SpreadTabProps {
  bettingVolumeData: BettingVolumeResponse | null | undefined;
  homeTeam: string;
  awayTeam: string;
}

// Format decimal to American odds
const formatAmericanOdds = (price: number): string => {
  if (price >= 2) {
    return `+${Math.round((price - 1) * 100)}`;
  }
  return `-${Math.round(100 / (price - 1))}`;
};

// Get short team name
const getShortName = (name: string): string => {
  const parts = name.split(' ');
  return parts[parts.length - 1];
};

// Key numbers in spread betting (NFL focused)
const KEY_NUMBERS = [3, 7, 10, 14, 17, 21];

export default function SpreadTab({
  bettingVolumeData,
  homeTeam,
  awayTeam,
}: SpreadTabProps) {
  // Find spreads market data
  const spreadsMarket = bettingVolumeData?.markets.find(m => m.marketType === 'spreads');

  if (!spreadsMarket) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-400">No spread data available</p>
        <p className="text-gray-500 text-sm mt-2">
          Spread data will appear when odds are collected from bookmakers
        </p>
      </div>
    );
  }

  // Group outcomes by point value
  const spreadsByPoint = new Map<number, {
    homeOdds: Array<{ bookmaker: string; price: number; isSharp: boolean }>;
    awayOdds: Array<{ bookmaker: string; price: number; isSharp: boolean }>;
  }>();

  spreadsMarket.outcomes.forEach(outcome => {
    const point = outcome.point ?? 0;
    const absPoint = Math.abs(point);

    if (!spreadsByPoint.has(absPoint)) {
      spreadsByPoint.set(absPoint, { homeOdds: [], awayOdds: [] });
    }

    const entry = spreadsByPoint.get(absPoint)!;
    const isHome = point < 0; // Negative spread = home favorite

    outcome.bookmakers.forEach(bookie => {
      const oddsEntry = { bookmaker: bookie.name, price: bookie.price, isSharp: bookie.isSharp };
      if (isHome) {
        entry.homeOdds.push(oddsEntry);
      } else {
        entry.awayOdds.push(oddsEntry);
      }
    });
  });

  // Get the main spread line (most common)
  const mainSpread = Array.from(spreadsByPoint.entries())
    .sort((a, b) => Math.max(b[1].homeOdds.length, b[1].awayOdds.length) - Math.max(a[1].homeOdds.length, a[1].awayOdds.length))[0];

  // Check if main spread is a key number
  const isKeyNumber = mainSpread ? KEY_NUMBERS.includes(mainSpread[0]) : false;

  return (
    <div className="space-y-6">
      {/* Main Spread Display */}
      <div className="card">
        <h2 className="text-xl font-bold mb-4">SPREAD LINES</h2>
        <p className="text-sm text-gray-400 mb-4">
          {spreadsMarket.bookmakerCount} bookmakers offering spread bets
        </p>

        {/* Current Spread Summary */}
        {mainSpread && (
          <div className="bg-surface-50 rounded-lg p-6 border border-gray-800 mb-6">
            <div className="text-center mb-4">
              <span className="text-xs text-gray-500 uppercase">Main Line</span>
              {isKeyNumber && (
                <span className="ml-2 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded">
                  Key Number
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-8">
              {/* Home Team */}
              <div className="text-center">
                <span className="text-lg font-bold text-blue-400">{getShortName(homeTeam)}</span>
                <div className="text-4xl font-bold font-mono mt-2 text-blue-400">
                  -{mainSpread[0]}
                </div>
                {mainSpread[1].homeOdds.length > 0 && (
                  <div className="text-sm text-gray-400 mt-2">
                    Best: {formatAmericanOdds(Math.max(...mainSpread[1].homeOdds.map(o => o.price)))}
                  </div>
                )}
              </div>

              {/* Away Team */}
              <div className="text-center">
                <span className="text-lg font-bold text-orange-400">{getShortName(awayTeam)}</span>
                <div className="text-4xl font-bold font-mono mt-2 text-orange-400">
                  +{mainSpread[0]}
                </div>
                {mainSpread[1].awayOdds.length > 0 && (
                  <div className="text-sm text-gray-400 mt-2">
                    Best: {formatAmericanOdds(Math.max(...mainSpread[1].awayOdds.map(o => o.price)))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Alternative Spreads */}
        {spreadsByPoint.size > 1 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">
              Alternative Spreads
            </h3>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Array.from(spreadsByPoint.entries())
                .sort((a, b) => a[0] - b[0])
                .map(([point, odds]) => {
                  const isKey = KEY_NUMBERS.includes(point);
                  const isMain = mainSpread && point === mainSpread[0];

                  return (
                    <div
                      key={point}
                      className={`p-3 rounded-lg border ${
                        isMain
                          ? 'bg-purple-500/10 border-purple-500/30'
                          : isKey
                            ? 'bg-yellow-500/5 border-yellow-500/20'
                            : 'bg-surface-50 border-gray-800'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-400">
                          {point === 0 ? 'PK' : `${point}`}
                        </span>
                        {isKey && !isMain && (
                          <span className="text-xs text-yellow-400">Key</span>
                        )}
                        {isMain && (
                          <span className="text-xs text-purple-400">Main</span>
                        )}
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-blue-400 font-mono">
                          {odds.homeOdds.length > 0
                            ? formatAmericanOdds(Math.max(...odds.homeOdds.map(o => o.price)))
                            : '-'}
                        </span>
                        <span className="text-orange-400 font-mono">
                          {odds.awayOdds.length > 0
                            ? formatAmericanOdds(Math.max(...odds.awayOdds.map(o => o.price)))
                            : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>{odds.homeOdds.length} books</span>
                        <span>{odds.awayOdds.length} books</span>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>

      {/* Bookmaker Breakdown Table */}
      <div className="card">
        <h2 className="text-xl font-bold mb-4">SPREAD BY BOOKMAKER</h2>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs text-gray-400 uppercase border-b border-gray-700">
                <th className="pb-3 pr-4">Bookmaker</th>
                <th className="pb-3 px-4 text-center">{getShortName(homeTeam)} Spread</th>
                <th className="pb-3 px-4 text-center">{getShortName(homeTeam)} Odds</th>
                <th className="pb-3 px-4 text-center">{getShortName(awayTeam)} Spread</th>
                <th className="pb-3 px-4 text-center">{getShortName(awayTeam)} Odds</th>
                <th className="pb-3 pl-4">Type</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Build bookmaker map
                const bookmakers = new Map<string, {
                  homeSpread?: number;
                  homePrice?: number;
                  awaySpread?: number;
                  awayPrice?: number;
                  isSharp: boolean;
                }>();

                spreadsMarket.outcomes.forEach(outcome => {
                  const point = outcome.point ?? 0;
                  outcome.bookmakers.forEach(bookie => {
                    if (!bookmakers.has(bookie.name)) {
                      bookmakers.set(bookie.name, { isSharp: bookie.isSharp });
                    }
                    const entry = bookmakers.get(bookie.name)!;
                    if (point < 0) {
                      entry.homeSpread = point;
                      entry.homePrice = bookie.price;
                    } else {
                      entry.awaySpread = point;
                      entry.awayPrice = bookie.price;
                    }
                  });
                });

                return Array.from(bookmakers.entries())
                  .sort((a, b) => (b[1].isSharp ? 1 : 0) - (a[1].isSharp ? 1 : 0))
                  .map(([name, data]) => (
                    <tr key={name} className="border-b border-gray-800 hover:bg-surface-50">
                      <td className="py-3 pr-4 font-medium">{name}</td>
                      <td className="py-3 px-4 text-center font-mono text-blue-400">
                        {data.homeSpread !== undefined ? data.homeSpread : '-'}
                      </td>
                      <td className="py-3 px-4 text-center font-mono text-blue-400">
                        {data.homePrice ? formatAmericanOdds(data.homePrice) : '-'}
                      </td>
                      <td className="py-3 px-4 text-center font-mono text-orange-400">
                        {data.awaySpread !== undefined ? `+${data.awaySpread}` : '-'}
                      </td>
                      <td className="py-3 px-4 text-center font-mono text-orange-400">
                        {data.awayPrice ? formatAmericanOdds(data.awayPrice) : '-'}
                      </td>
                      <td className="py-3 pl-4">
                        <span className={`px-2 py-1 text-xs rounded ${
                          data.isSharp
                            ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                            : 'bg-gray-700/50 text-gray-400'
                        }`}>
                          {data.isSharp ? 'Sharp' : 'Square'}
                        </span>
                      </td>
                    </tr>
                  ));
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* Key Numbers Info */}
      <div className="card">
        <h2 className="text-xl font-bold mb-4">KEY NUMBERS</h2>
        <p className="text-sm text-gray-400 mb-4">
          Important spread values where games frequently land
        </p>

        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {KEY_NUMBERS.map(num => {
            const hasData = spreadsByPoint.has(num);
            const isCurrentSpread = mainSpread && mainSpread[0] === num;

            return (
              <div
                key={num}
                className={`p-3 rounded-lg text-center border ${
                  isCurrentSpread
                    ? 'bg-purple-500/20 border-purple-500'
                    : hasData
                      ? 'bg-yellow-500/10 border-yellow-500/30'
                      : 'bg-surface-50 border-gray-800'
                }`}
              >
                <div className={`text-2xl font-bold font-mono ${
                  isCurrentSpread
                    ? 'text-purple-400'
                    : hasData
                      ? 'text-yellow-400'
                      : 'text-gray-600'
                }`}>
                  {num}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {isCurrentSpread ? 'Current' : hasData ? 'Available' : '-'}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
